import { create } from 'zustand';
import { registerPlanFeatures } from '../config/plans';

// ─── Types ───────────────────────────────────────────────────────
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  staffName?: string;
  storeType?: string;
  phoneNumber?: string;
}

export interface Subscription {
  id?: string;
  plan: string;
  status: string;
  trialEnd?: string;
  endDate?: string;
  maxCustomers?: number;
  maxStaff?: number;
  planName?: string;
  autoRenew?: boolean;
  exempt?: boolean;
  features?: string[];
}

// ─── Auth Store ──────────────────────────────────────────────────
interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  subscription: Subscription | null;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  setSubscription: (sub: Subscription) => void;
  logout: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const getStoredValue = <T>(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    return JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
};

export const useAuthStore = create<AuthStore>((set) => ({
  user: getStoredValue<User | null>('user', null),
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  subscription: getStoredValue<Subscription | null>('subscription', null),
  isLoading: false,

  setUser: (user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
    set({ user, isAuthenticated: true });
  },

  setToken: (token: string) => {
    localStorage.setItem('token', token);
    set({ token, isAuthenticated: true });
  },

  setSubscription: (sub: Subscription) => {
    // Keep the module feature map in sync with the live package catalog.
    registerPlanFeatures(sub?.plan, sub?.features);
    localStorage.setItem('subscription', JSON.stringify(sub));
    set({ subscription: sub });
  },

  logout: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('subscription');
    set({ user: null, token: null, subscription: null, isAuthenticated: false });
  },

  setIsLoading: (loading: boolean) => set({ isLoading: loading }),
}));

// Re-hydrate live plan features from the persisted session.
{
  const storedSub = getStoredValue<Subscription | null>('subscription', null);
  if (storedSub) registerPlanFeatures(storedSub.plan, storedSub.features);
}

// ─── Customer Store ──────────────────────────────────────────────
interface CustomerStore {
  currentCustomer: any | null;
  customers: any[];
  setCurrentCustomer: (customer: any) => void;
  setCustomers: (customers: any[]) => void;
  clearCurrentCustomer: () => void;
}

export const useCustomerStore = create<CustomerStore>((set) => ({
  currentCustomer: null,
  customers: [],

  setCurrentCustomer: (customer: any) => set({ currentCustomer: customer }),
  setCustomers: (customers: any[]) => set({ customers }),
  clearCurrentCustomer: () => set({ currentCustomer: null }),
}));

// ─── UI Store ────────────────────────────────────────────────────
interface UIStore {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  activeModal: string | null;
  setActiveModal: (modal: string | null) => void;
  toasts: Array<{ id: string; message: string; type: 'success' | 'error' | 'info' }>;
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
  removeToast: (id: string) => void;
}

export const useUIStore = create<UIStore>((set) => ({
  sidebarOpen: false,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open: boolean) => set({ sidebarOpen: open }),

  activeModal: null,
  setActiveModal: (modal: string | null) => set({ activeModal: modal }),

  toasts: [],
  addToast: (message: string, type: 'success' | 'error' | 'info') => {
    const id = Date.now().toString(36) + Math.random().toString(36).substr(2);
    set((state) => ({
      toasts: [...state.toasts, { id, message, type }],
    }));
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }, 4000);
  },
  removeToast: (id: string) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),
}));
